import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ArrowLeft, Layers, Search, Plus, ChevronRight, PieChart, Pencil,
  ChevronDown, ChevronUp, X, Heart, CheckCircle2, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const SDG_GOALS = [
  { n: 1, label: '1. No Poverty' }, { n: 2, label: '2. Zero Hunger' },
  { n: 3, label: '3. Good Health' }, { n: 4, label: '4. Education' },
  { n: 5, label: '5. Gender Equality' }, { n: 6, label: '6. Clean Water' },
  { n: 7, label: '7. Clean Energy' }, { n: 8, label: '8. Decent Work' },
  { n: 9, label: '9. Innovation' }, { n: 10, label: '10. Reduced Inequalities' },
  { n: 11, label: '11. Sustainable Cities' }, { n: 12, label: '12. Responsible Consumption' },
  { n: 13, label: '13. Climate Action' }, { n: 14, label: '14. Life Below Water' },
  { n: 15, label: '15. Life on Land' }, { n: 16, label: '16. Peace & Justice' },
  { n: 17, label: '17. Partnerships' },
];

/**
 * Optional plan-level overrides. They win over segment-derived defaults during
 * Blended-Investment → Social-Investment-Registry conversion. Rendered inside
 * a collapsible section so the core form stays focused.
 */
const PlanMetadataSection = ({ value, onChange, testIdPrefix = 'plan-meta' }) => {
  const [open, setOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const sdgSet = new Set(value.sdg_alignment || []);
  const toggleSdg = (n) => {
    const next = new Set(sdgSet);
    if (next.has(n)) next.delete(n); else next.add(n);
    onChange({ ...value, sdg_alignment: Array.from(next).sort((a, b) => a - b) });
  };
  const addTag = () => {
    const t = (tagInput || '').trim();
    if (!t) return;
    const existing = value.tags || [];
    if (existing.includes(t)) { setTagInput(''); return; }
    onChange({ ...value, tags: [...existing, t] });
    setTagInput('');
  };
  const removeTag = (t) => onChange({ ...value, tags: (value.tags || []).filter((x) => x !== t) });

  return (
    <div className="border rounded-lg bg-slate-50/50">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testIdPrefix}-toggle`}
      >
        <span>Plan-level metadata (optional overrides)</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="p-3 space-y-3 border-t">
          <p className="text-xs text-slate-700 italic">
            Fields filled here win over segment-derived defaults when the plan is later registered in the Social Investment Registry or converted to a Project.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor={`${testIdPrefix}-sector-input`} className="text-xs">Sector</Label><Input id={`${testIdPrefix}-sector-input`} aria-label="Sector" value={value.sector || ''} onChange={(e) => onChange({ ...value, sector: e.target.value })} placeholder="e.g., Education, Health" data-testid={`${testIdPrefix}-sector`} /></div>
            <div><Label htmlFor={`${testIdPrefix}-location-input`} className="text-xs">Location</Label><Input id={`${testIdPrefix}-location-input`} aria-label="Location" value={value.location || ''} onChange={(e) => onChange({ ...value, location: e.target.value })} placeholder="City / Region" data-testid={`${testIdPrefix}-location`} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label htmlFor={`${testIdPrefix}-benef-input`} className="text-xs">Target beneficiaries</Label><Input id={`${testIdPrefix}-benef-input`} aria-label="Target beneficiaries" type="number" min="0" value={value.target_beneficiary_count ?? ''} onChange={(e) => onChange({ ...value, target_beneficiary_count: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 })} data-testid={`${testIdPrefix}-benef`} /></div>
            <div><Label htmlFor={`${testIdPrefix}-months-input`} className="text-xs">Duration (months)</Label><Input id={`${testIdPrefix}-months-input`} aria-label="Duration in months" type="number" min="0" value={value.timeline_months ?? ''} onChange={(e) => onChange({ ...value, timeline_months: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 })} data-testid={`${testIdPrefix}-months`} /></div>
            <div><Label htmlFor={`${testIdPrefix}-start-input`} className="text-xs">Start date</Label><Input id={`${testIdPrefix}-start-input`} aria-label="Start date" type="date" value={value.start_date || ''} onChange={(e) => onChange({ ...value, start_date: e.target.value })} data-testid={`${testIdPrefix}-start`} /></div>
          </div>
          <div>
            <Label className="text-xs">SDG Alignment</Label>
            <div className="grid grid-cols-3 gap-1 mt-1 max-h-36 overflow-y-auto border rounded p-2 bg-white">
              {SDG_GOALS.map((s) => (
                <label key={s.n} className="flex items-center gap-1 text-xs">
                  <Checkbox aria-label={`SDG ${s.n}: ${s.label}`} checked={sdgSet.has(s.n)} onCheckedChange={() => toggleSdg(s.n)} data-testid={`${testIdPrefix}-sdg-${s.n}`} />
                  <span>{s.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor={`${testIdPrefix}-tag-input-el`} className="text-xs">Tags</Label>
            <div className="flex gap-2 mt-1">
              <Input id={`${testIdPrefix}-tag-input-el`} aria-label="Add a tag" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Type a tag and press Enter" data-testid={`${testIdPrefix}-tag-input`} />
              <Button type="button" size="sm" variant="outline" onClick={addTag} aria-label="Add tag" data-testid={`${testIdPrefix}-tag-add`}><Plus className="w-3 h-3" aria-hidden="true" /></Button>
            </div>
            {(value.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {(value.tags || []).map((t) => (
                  <Badge key={t} variant="outline" className="text-xs pr-1">
                    {t}
                    <button type="button" className="ml-1 text-slate-700 hover:text-rose-700" onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                      <X className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const EMPTY_META = { sdg_alignment: [], sector: '', location: '', target_beneficiary_count: '', timeline_months: '', start_date: '', tags: [] };

const buildMetaPayload = (state) => {
  // Convert UI state into the backend payload shape. Only send non-empty overrides.
  const out = {};
  if (Array.isArray(state.sdg_alignment) && state.sdg_alignment.length > 0) out.sdg_alignment = state.sdg_alignment;
  if (state.sector) out.sector = state.sector;
  if (state.location) out.location = state.location;
  if (state.target_beneficiary_count !== '' && state.target_beneficiary_count != null) out.target_beneficiary_count = parseInt(state.target_beneficiary_count, 10) || 0;
  if (state.timeline_months !== '' && state.timeline_months != null) out.timeline_months = parseInt(state.timeline_months, 10) || 0;
  if (state.start_date) out.start_date = state.start_date;
  if (Array.isArray(state.tags) && state.tags.length > 0) out.tags = state.tags;
  return out;
};

const CombinedInvestments = ({ user }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [view, setView] = useState('plans'); // 'plans' | 'opportunities'
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlan, setNewPlan] = useState({ title: '', description: '', investment_title: '', total_budget: '', theory_of_change: '', ...EMPTY_META });

  // Plan-level "Express Interest" — set of plan_ids the current user has
  // already flagged + dialog state for new submissions.
  const [myInterestPlanIds, setMyInterestPlanIds] = useState(new Set());
  const [interestDialog, setInterestDialog] = useState({ open: false, plan: null });
  const [interestForm, setInterestForm] = useState({ message: '', amount: '', contact_email: '', contact_phone: '' });

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statusFilter !== 'all') params.append('status', statusFilter);
        const [plansRes, oppsRes, mineRes] = await Promise.all([
          axios.get(`${API}/api/blended-investments?${params}`, { headers }),
          axios.get(`${API}/api/blended-investments/open`, { headers }).catch(() => ({ data: { opportunities: [] } })),
          axios.get(`${API}/api/blended-investments/my-plan-interests`, { headers }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setPlans(plansRes.data.plans || []);
        setStats(plansRes.data.stats || {});
        setOpportunities(oppsRes.data?.opportunities || []);
        setMyInterestPlanIds(new Set((mineRes.data || []).map((i) => i.plan_id)));
      } catch (err) {
        if (cancelled) return;
        setPlans([]);
        setStats({ total: 0, by_status: {} });
        setOpportunities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [statusFilter, headers]);

  const fetchPlans = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/api/blended-investments?${params}`, { headers });
      setPlans(res.data.plans || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const res = await axios.get(`${API}/api/blended-investments/open`, { headers });
      setOpportunities(res.data?.opportunities || []);
    } catch (e) { /* non-fatal */ }
  };

  const openInterestDialog = (plan) => {
    const initial = { message: '', amount: '', contact_email: user?.email || '', contact_phone: '' };
    setInterestForm(initial);
    setInterestDialog({ open: true, plan });
  };

  const submitInterest = async () => {
    if (!interestForm.message.trim()) {
      toast.error('Please write a short message');
      return;
    }
    const plan = interestDialog.plan;
    try {
      await axios.post(`${API}/api/blended-investments/${plan.id}/express-interest`, {
        message: interestForm.message.trim(),
        amount: interestForm.amount ? parseFloat(interestForm.amount) : null,
        contact_email: interestForm.contact_email || '',
        contact_phone: interestForm.contact_phone || '',
      }, { headers });
      toast.success('Interest sent to the plan creator');
      setInterestDialog({ open: false, plan: null });
      setMyInterestPlanIds((prev) => new Set([...prev, plan.id]));
      fetchPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send interest');
    }
  };

  const handleCreate = async () => {
    if (!newPlan.title || !newPlan.total_budget) {
      toast.error('Title and budget are required');
      return;
    }
    try {
      const payload = {
        title: newPlan.title,
        description: newPlan.description,
        investment_title: newPlan.investment_title,
        total_budget: parseFloat(newPlan.total_budget),
        theory_of_change: newPlan.theory_of_change,
        ...buildMetaPayload(newPlan),
      };
      await axios.post(`${API}/api/blended-investments`, payload, { headers });
      toast.success('Blended plan created');
      setShowCreateDialog(false);
      setNewPlan({ title: '', description: '', investment_title: '', total_budget: '', theory_of_change: '', ...EMPTY_META });
      fetchPlans();
    } catch (error) {
      toast.error('Failed to create plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan({
      ...plan,
      total_budget: plan.total_budget || '',
      sdg_alignment: Array.isArray(plan.sdg_alignment) ? plan.sdg_alignment : [],
      sector: plan.sector || '',
      location: plan.location || '',
      target_beneficiary_count: plan.target_beneficiary_count != null ? plan.target_beneficiary_count : '',
      timeline_months: plan.timeline_months != null ? plan.timeline_months : '',
      start_date: plan.start_date || '',
      tags: Array.isArray(plan.tags) ? plan.tags : [],
    });
  };

  const handleUpdate = async () => {
    if (!editingPlan || !editingPlan.title || !editingPlan.total_budget) {
      toast.error('Title and budget are required');
      return;
    }
    try {
      const payload = {
        title: editingPlan.title,
        description: editingPlan.description || '',
        investment_title: editingPlan.investment_title || '',
        total_budget: parseFloat(editingPlan.total_budget),
        currency: editingPlan.currency || 'EUR',
        theory_of_change: editingPlan.theory_of_change || '',
        // Always send the override fields on edit so explicit clears ([] / '') take effect
        sdg_alignment: Array.isArray(editingPlan.sdg_alignment) ? editingPlan.sdg_alignment : [],
        sector: editingPlan.sector || '',
        location: editingPlan.location || '',
        target_beneficiary_count: editingPlan.target_beneficiary_count === '' || editingPlan.target_beneficiary_count == null
          ? 0
          : parseInt(editingPlan.target_beneficiary_count, 10) || 0,
        timeline_months: editingPlan.timeline_months === '' || editingPlan.timeline_months == null
          ? 0
          : parseInt(editingPlan.timeline_months, 10) || 0,
        start_date: editingPlan.start_date || '',
        tags: Array.isArray(editingPlan.tags) ? editingPlan.tags : [],
      };
      await axios.put(`${API}/api/blended-investments/${editingPlan.id}`, payload, { headers });
      toast.success('Plan updated');
      setEditingPlan(null);
      fetchPlans();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const filteredPlans = plans.filter(p => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return p.title?.toLowerCase().includes(s) || p.investment_title?.toLowerCase().includes(s);
  });

  const formatCurrency = (amount, currency = 'EUR') => {
    if (!amount && amount !== 0) return 'N/A';
    return `${currency} ${new Intl.NumberFormat('el-GR', { maximumFractionDigits: 0 }).format(amount)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
  };

  const total = stats?.total || 0;
  const counts = stats?.by_status || {};

  const STATUS_TILES = [
    { key: 'draft',      label: 'Draft',      cls: 'bg-slate-50 text-slate-700 border-slate-200',   active: 'ring-2 ring-slate-400' },
    { key: 'open',       label: 'Open',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', active: 'ring-2 ring-emerald-400' },
    { key: 'in_funding', label: 'In Funding', cls: 'bg-amber-50 text-amber-700 border-amber-200',   active: 'ring-2 ring-amber-400' },
    { key: 'funded',     label: 'Funded',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-200', active: 'ring-2 ring-indigo-400' },
    { key: 'active',     label: 'Active',     cls: 'bg-blue-50 text-blue-700 border-blue-200',     active: 'ring-2 ring-blue-400' },
    { key: 'completed',  label: 'Completed',  cls: 'bg-purple-50 text-purple-700 border-purple-200', active: 'ring-2 ring-purple-400' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Layers className="h-7 w-7 text-indigo-600" /> Blended Investments
            </h1>
            <p className="text-gray-700 mt-1">
              Segment investments into multiple funding sources and track blended finance
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="new-plan-btn">
          <Plus className="h-4 w-4 mr-2" /> New Blended Plan
        </Button>
      </div>

      {/* Stats Cards — clickable to filter by status */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card
          className={`cursor-pointer transition-shadow hover:shadow-md border-blue-100 bg-blue-50/30 ${statusFilter === 'all' ? 'ring-2 ring-blue-400' : ''}`}
          onClick={() => { setStatusFilter('all'); setView('plans'); }}
          data-testid="stat-tile-all"
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{total}</p>
            <p className="text-xs text-gray-700">All</p>
          </CardContent>
        </Card>
        {STATUS_TILES.map((t) => (
          <Card
            key={t.key}
            className={`cursor-pointer transition-shadow hover:shadow-md ${t.cls} border ${statusFilter === t.key ? t.active : ''}`}
            onClick={() => { setStatusFilter(t.key); setView('plans'); }}
            data-testid={`stat-tile-${t.key}`}
          >
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{counts[t.key] || 0}</p>
              <p className="text-xs">{t.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View toggle: Plans / Open Opportunities */}
      <div className="flex gap-2">
        <Button
          variant={view === 'plans' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('plans')}
          data-testid="view-plans"
        >
          <Layers className="w-4 h-4 mr-2" /> All Plans
        </Button>
        <Button
          variant={view === 'opportunities' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('opportunities')}
          data-testid="view-opportunities"
        >
          <PieChart className="w-4 h-4 mr-2" /> Open Opportunities ({opportunities.length})
        </Button>
      </div>

      {/* Search & Filter */}
      {view === 'plans' && (
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-700" aria-hidden="true" />
              <Input
                data-testid="search-plans"
                aria-label="Search blended plans"
                placeholder="Search blended plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" aria-label="Filter by status" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_funding">In Funding</SelectItem>
                <SelectItem value="funded">Funded</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Plan List */}
      {view === 'plans' && (
        <section aria-labelledby="blended-plans-heading">
          <h2 id="blended-plans-heading" className="sr-only">Blended plans list</h2>
          {filteredPlans.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <PieChart className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Blended Plans</h3>
            <p className="text-gray-700 text-center max-w-md mb-4">
              Create your first blended investment plan to start segmenting investments across funding sources.
            </p>
            <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="h-4 w-4 mr-2" aria-hidden="true" /> New Blended Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPlans.map((plan) => {
            const allocPct = plan.total_budget > 0 ? Math.round((plan.allocated_budget / plan.total_budget) * 100) : 0;
            const isOwn = plan.creator_id === user?.id;
            const alreadyInterested = myInterestPlanIds.has(plan.id);
            const canExpressInterest = !isOwn && plan.status !== 'cancelled' && plan.status !== 'completed';
            return (
              <Card key={plan.id} data-testid={`plan-${plan.id}`} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/combined-investments/${plan.id}`)}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs capitalize">{(plan.status || '').replace('_',' ')}</Badge>
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Layers className="h-3 w-3" /> {plan.segment_count} segments
                      </Badge>
                      {plan.interest_count > 0 && (
                        <Badge variant="outline"
                               className="text-xs flex items-center gap-1 bg-rose-50 text-rose-700 border-rose-200"
                               data-testid={`interest-count-${plan.id}`}>
                          <Heart className="h-3 w-3" /> {plan.interest_count} interested
                        </Badge>
                      )}
                      {alreadyInterested && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="h-3 w-3" /> You expressed interest
                        </Badge>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </div>

                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">{plan.title}</h3>
                    <div className="flex items-center gap-1">
                      {canExpressInterest && !alreadyInterested && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={(e) => { e.stopPropagation(); openInterestDialog(plan); }}
                          data-testid={`express-interest-${plan.id}`}
                        >
                          <Heart className="h-4 w-4 mr-1" aria-hidden="true" /> Express interest
                        </Button>
                      )}
                      {isOwn && (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(plan); }} aria-label={`Edit plan ${plan.title}`} data-testid={`edit-plan-${plan.id}`}>
                          <Pencil className="h-4 w-4 text-gray-700" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{plan.investment_title}</p>

                  <div className="flex items-center justify-between text-sm text-gray-700 mb-2">
                    <span>{formatCurrency(plan.allocated_budget)} / {formatCurrency(plan.total_budget)}</span>
                    <span>{allocPct}% allocated</span>
                  </div>
                  <Progress value={allocPct} className="h-2.5 mb-2" />

                  <p className="text-xs text-gray-700">by {plan.creator_username} · {formatDate(plan.created_at)}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        </section>
      )}

      {/* Open Opportunities view */}
      {view === 'opportunities' && (
        <section aria-labelledby="open-opportunities-heading">
          <h2 id="open-opportunities-heading" className="sr-only">Open opportunities list</h2>
          {opportunities.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <PieChart className="h-12 w-12 text-gray-400 mb-4" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Open Opportunities</h3>
              <p className="text-gray-700 text-center max-w-md">
                Plans appear here once their author promotes them to investors.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {opportunities.map((opp) => {
              const allocPct = opp.total_budget > 0 ? Math.round((opp.allocated_budget / opp.total_budget) * 100) : 0;
              const committedPct = opp.allocated_budget > 0 ? Math.round((opp.committed_amount / opp.allocated_budget) * 100) : 0;
              return (
                <Card
                  key={opp.id}
                  data-testid={`opportunity-${opp.id}`}
                  className="hover:shadow-md transition-shadow cursor-pointer border-emerald-100"
                  onClick={() => navigate(`/combined-investments/${opp.id}`)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border text-xs capitalize">{(opp.status || '').replace('_', ' ')}</Badge>
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <Layers className="h-3 w-3" aria-hidden="true" /> {opp.segment_count} segments
                        </Badge>
                        {opp.invited_investor_ids?.length > 0 ? (
                          <Badge variant="outline" className="text-xs">By invitation</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-emerald-50">Open to all</Badge>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-700" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{opp.title}</h3>
                    <p className="text-sm text-gray-700 mb-2">{opp.investment_title}</p>
                    {opp.promoted_message && (
                      <p className="text-xs italic text-gray-800 bg-emerald-50 border border-emerald-200 rounded p-2 mb-3">&ldquo;{opp.promoted_message}&rdquo;</p>
                    )}
                    <div className="flex items-center justify-between text-sm text-gray-700 mb-1">
                      <span>{formatCurrency(opp.committed_amount)} committed of {formatCurrency(opp.allocated_budget)} ({committedPct}%)</span>
                      <span>{allocPct}% of total {formatCurrency(opp.total_budget)}</span>
                    </div>
                    <Progress value={committedPct} className="h-2.5 mb-2" />
                    <p className="text-xs text-gray-700">by {opp.creator_username} · promoted {formatDate(opp.promoted_at)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        </section>
      )}

      {/* Create Plan Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Blended Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="new-plan-title">Plan Title *</Label>
              <Input id="new-plan-title" aria-label="Plan title" value={newPlan.title} onChange={(e) => setNewPlan({ ...newPlan, title: e.target.value })} placeholder="e.g., Blended Plan: Digital Inclusion" data-testid="plan-title-input" />
            </div>
            <div>
              <Label htmlFor="new-plan-investment-title">Investment Title</Label>
              <Input id="new-plan-investment-title" aria-label="Investment title" value={newPlan.investment_title} onChange={(e) => setNewPlan({ ...newPlan, investment_title: e.target.value })} placeholder="e.g., Digital Inclusion for Rural Elders" />
            </div>
            <div>
              <Label htmlFor="new-plan-budget">Total Budget (EUR) *</Label>
              <Input id="new-plan-budget" aria-label="Total budget in EUR" type="number" value={newPlan.total_budget} onChange={(e) => setNewPlan({ ...newPlan, total_budget: e.target.value })} placeholder="50000" data-testid="plan-budget-input" />
            </div>
            <div>
              <Label htmlFor="new-plan-description">Description</Label>
              <Textarea id="new-plan-description" aria-label="Description" value={newPlan.description} onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })} placeholder="Multi-source funding plan..." rows={2} />
            </div>
            <div>
              <Label htmlFor="new-plan-toc">Theory of Change</Label>
              <Textarea id="new-plan-toc" aria-label="Theory of change" value={newPlan.theory_of_change} onChange={(e) => setNewPlan({ ...newPlan, theory_of_change: e.target.value })} placeholder="If we... then..." rows={2} />
            </div>

            <PlanMetadataSection value={newPlan} onChange={setNewPlan} testIdPrefix="plan-create-meta" />

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-emerald-700 hover:bg-emerald-800" data-testid="submit-plan-btn">Create Plan</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingPlan} onOpenChange={() => setEditingPlan(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Blended Plan</DialogTitle></DialogHeader>
          {editingPlan && (
            <div className="space-y-4 pt-2">
              <div><Label htmlFor="edit-plan-title-input">Plan Title *</Label><Input id="edit-plan-title-input" aria-label="Plan title" value={editingPlan.title} onChange={(e) => setEditingPlan({ ...editingPlan, title: e.target.value })} data-testid="edit-plan-title" /></div>
              <div><Label htmlFor="edit-plan-inv-title">Investment Title</Label><Input id="edit-plan-inv-title" aria-label="Investment title" value={editingPlan.investment_title || ''} onChange={(e) => setEditingPlan({ ...editingPlan, investment_title: e.target.value })} /></div>
              <div><Label htmlFor="edit-plan-budget-input">Total Budget (EUR) *</Label><Input id="edit-plan-budget-input" aria-label="Total budget in EUR" type="number" value={editingPlan.total_budget} onChange={(e) => setEditingPlan({ ...editingPlan, total_budget: e.target.value })} data-testid="edit-plan-budget" /></div>
              <div><Label htmlFor="edit-plan-desc">Description</Label><Textarea id="edit-plan-desc" aria-label="Description" value={editingPlan.description || ''} onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })} rows={2} /></div>
              <div><Label htmlFor="edit-plan-toc">Theory of Change</Label><Textarea id="edit-plan-toc" aria-label="Theory of change" value={editingPlan.theory_of_change || ''} onChange={(e) => setEditingPlan({ ...editingPlan, theory_of_change: e.target.value })} rows={2} /></div>

              <PlanMetadataSection value={editingPlan} onChange={setEditingPlan} testIdPrefix="plan-edit-meta" />

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditingPlan(null)}>Cancel</Button>
                <Button onClick={handleUpdate} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-plan-edit-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Express Interest Dialog (plan-level) */}
      <Dialog
        open={interestDialog.open}
        onOpenChange={(o) => !o && setInterestDialog({ open: false, plan: null })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-rose-600" /> Express interest
            </DialogTitle>
          </DialogHeader>
          {interestDialog.plan && (
            <div className="space-y-3 pt-1">
              <div className="text-xs text-slate-700">
                Plan: <span className="font-medium text-slate-800">{interestDialog.plan.title}</span>
                <br />
                Creator: <span className="font-medium text-slate-800">@{interestDialog.plan.creator_username}</span>
              </div>
              <div>
                <Label htmlFor="interest-message">Message *</Label>
                <Textarea
                  id="interest-message"
                  aria-label="Interest message"
                  rows={4}
                  placeholder="Tell the plan creator how you could collaborate, your relevant experience, or what scope you propose."
                  value={interestForm.message}
                  onChange={(e) => setInterestForm({ ...interestForm, message: e.target.value })}
                  data-testid="interest-message-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="interest-amount">Indicative amount (optional)</Label>
                  <Input
                    id="interest-amount"
                    aria-label="Indicative amount in EUR"
                    type="number" min="0" step="1000"
                    value={interestForm.amount}
                    onChange={(e) => setInterestForm({ ...interestForm, amount: e.target.value })}
                    placeholder="EUR"
                    data-testid="interest-amount-input"
                  />
                </div>
                <div>
                  <Label htmlFor="interest-phone">Contact phone (optional)</Label>
                  <Input
                    id="interest-phone"
                    aria-label="Contact phone"
                    value={interestForm.contact_phone}
                    onChange={(e) => setInterestForm({ ...interestForm, contact_phone: e.target.value })}
                    placeholder="+30 ..."
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="interest-email">Contact email</Label>
                <Input
                  id="interest-email"
                  aria-label="Contact email"
                  type="email"
                  value={interestForm.contact_email}
                  onChange={(e) => setInterestForm({ ...interestForm, contact_email: e.target.value })}
                  placeholder="you@example.org"
                />
              </div>
              <p className="text-xs text-slate-700 -mt-1">
                <MessageCircle className="h-3 w-3 inline -mt-0.5" aria-hidden="true" /> The plan
                creator will receive an in-app notification with your message.
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setInterestDialog({ open: false, plan: null })}>
                  Cancel
                </Button>
                <Button
                  onClick={submitInterest}
                  className="bg-rose-600 hover:bg-rose-700"
                  data-testid="interest-submit-btn"
                >
                  <Heart className="h-4 w-4 mr-1" /> Send interest
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CombinedInvestments;
